// The notification template editor, as the wireframe draws it: a full page at
// its own address, not a dialog.
// per wireframes/react/src/pages/AdminAlerte.tsx:2834-2960 (editorul de șablon,
//   secțiunea Conținut și inseratorul „Valori din evaluare")
// per docs/ui-shape-admin-aggregations-alerts.md §Șabloane
// per docs/caiet-uc-spec.md §UC27-02 §UC-ALRT-04
//
// Why a page and not a dialog. The wireframe's route is `sabloane/:id/edit` and
// the audit found `/alert-templates/:id/edit` answering 404 — an address the
// design hands out and the application does not answer. A body template is the
// longest free text in the administration and it is written beside a list of
// twenty-odd substituents; a dialog that has to scroll both is the wrong
// container for it, which is why the wireframe stopped using one.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudTextInput, MudSelect, MudTextarea } from '@egov-moldova/mud-react';
import { useToast } from '@simsm/ui-shared';
import { type ApiRefusal, apiClient, apiRefusal, loadFailureKind } from '../../app/api-client';
import { FormField, FormPage, FormSection } from '../../components/FormPage/FormPage';
import { metadataToText, textToMetadata } from './notification-metadata';
import { TEMPLATE_PLACEHOLDERS } from './notification-placeholders';
import { TEMPLATE_LOCALES, localeLabelKey } from './notification-template-list';
import { technicalName } from './AlertRuleFormPage';

interface TemplateDto {
  id: string;
  name: string;
  displayName: string;
  titleTpl: string;
  bodyTpl: string;
  locale: string;
  metadata: Record<string, unknown>;
}

interface FormState {
  name: string;
  displayName: string;
  titleTpl: string;
  bodyTpl: string;
  locale: string;
  metadataText: string;
}

const EMPTY: FormState = {
  name: '',
  displayName: '',
  titleTpl: '',
  bodyTpl: '',
  locale: 'ro',
  metadataText: '',
};

/** The three fields a substituent can be inserted into (the wireframe's
 *  `templateTarget`). The technical name and the locale are not free text. */
type InsertTarget = 'titleTpl' | 'bodyTpl' | 'metadataText';

const INSERT_TARGET_KEYS: Record<InsertTarget, string> = {
  titleTpl: 'alerts.templates.field.title_tpl',
  bodyTpl: 'alerts.templates.field.body_tpl',
  metadataText: 'alerts.templates.field.metadata',
};

/**
 * The text after inserting `token` at `caret`. Split out because it is the only
 * part of the inserter worth testing: a caret the browser could not report
 * (`null`) appends rather than silently writing at position 0, which would put
 * the substituent in front of everything already typed.
 */
/** All the inserter needs of an AGE control: the root its real input lives in. */
type CaretHost = { shadowRoot: ShadowRoot | null };

export function insertAtCaret(value: string, caret: number | null, token: string): string {
  if (caret === null || caret < 0 || caret > value.length) {
    return `${value}${token}`;
  }
  return `${value.slice(0, caret)}${token}${value.slice(caret)}`;
}

export function NotificationTemplateFormPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const isNew = !id;

  const [form, setForm] = useState<FormState>(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);
  // Which field the administrator was last **in**. `null` until they have been
  // in one: an unfocused control reports `selectionStart === 0`, so honouring
  // its caret before it has ever held one writes the first substituent in front
  // of everything already in the body. Corp is still the field an untouched
  // form inserts into — the wireframe's default — it is just appended to.
  const [target, setTarget] = useState<InsertTarget | null>(null);
  const activeTarget: InsertTarget = target ?? 'bodyTpl';

  // The AGE hosts, so the inserter can ask the focused one where its caret is.
  // Held as the one thing this page reads off them — a shadow root — rather than
  // as their element classes: those classes do not satisfy `HTMLElement` under
  // `exactOptionalPropertyTypes` (their `ariaLabel` is optional), and the ref is
  // not the place to argue about that.
  const titleRef = useRef<CaretHost | null>(null);
  const bodyRef = useRef<CaretHost | null>(null);
  const metadataRef = useRef<CaretHost | null>(null);

  // There is no read-one route for a template — the register is one list and it
  // is short — so the editor reads the same list under the same query key. That
  // makes opening the editor from the register free, and it keeps this page
  // from being the reason a GET :id has to exist.
  const templates = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () =>
      apiClient
        .get<{ templates: TemplateDto[] }>('/admin/notification-templates')
        .then((r) => r.data.templates),
  });

  const existing = isNew ? undefined : templates.data?.find((template) => template.id === id);

  useEffect(() => {
    if (isNew || !existing) return;
    setForm({
      name: existing.name,
      displayName: existing.displayName,
      titleTpl: existing.titleTpl,
      bodyTpl: existing.bodyTpl,
      locale: existing.locale,
      metadataText: metadataToText(existing.metadata),
    });
  }, [isNew, existing]);

  function edit(patch: Partial<FormState>): void {
    setForm((current) => ({ ...current, ...patch }));
    setDirty(true);
  }

  const save = useMutation({
    mutationFn: (payload: FormState) => {
      const body = {
        displayName: payload.displayName.trim(),
        titleTpl: payload.titleTpl,
        bodyTpl: payload.bodyTpl,
        locale: payload.locale.trim(),
        metadata: textToMetadata(payload.metadataText),
      };
      // The technical name is derived from the name at creation (owner ruling,
      // 2026-09-10: the wireframe has no such field) and never changes after.
      return isNew
        ? apiClient
            .post<TemplateDto>('/admin/notification-templates', {
              name: technicalName(payload.displayName, Date.now().toString(36).slice(-4)),
              ...body,
            })
            .then((r) => r.data)
        : apiClient
            .patch<TemplateDto>(`/admin/notification-templates/${id}`, body)
            .then((r) => r.data);
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
      setDirty(false);
      toast({
        variant: 'success',
        title: isNew ? t('alerts.toast.template.created') : t('alerts.toast.template.saved'),
        text: saved.displayName,
        closeLabel: t('common.close'),
      });
      navigate('/system-admin/alert-templates');
    },
    onError: (error: unknown) =>
      setRefusal(
        apiRefusal(
          error,
          isNew ? 'alerts.templates.error.create_failed' : 'alerts.templates.error.save_failed',
        ),
      ),
  });

  const displayNameError = !form.displayName.trim()
    ? t('alerts.templates.error.display_name_required')
    : null;
  const titleError = !form.titleTpl.trim() ? t('alerts.templates.error.title_required') : null;
  const bodyError = !form.bodyTpl.trim() ? t('alerts.templates.error.body_required') : null;

  // The three interface languages; a template that arrived by import in another one keeps
  // it as a fourth, so that opening and saving it does not change its language.
  function localeOptions(current: string): Array<{ value: string; label: string }> {
    const known = TEMPLATE_LOCALES.map((locale) => ({
      value: locale,
      label: t(localeLabelKey(locale) ?? locale),
    }));
    return current && !TEMPLATE_LOCALES.some((locale) => locale === current)
      ? [...known, { value: current, label: current }]
      : known;
  }

  function handleSave(): void {
    setSubmitted(true);
    setRefusal(null);
    if (displayNameError || titleError || bodyError) return;
    save.mutate(form);
  }

  // AGE-GAP(no-caret-api) · vendor/age-design/SOURCE_COMMIT 7322a157 — `mud-text-input`
  // and `mud-textarea` expose a value and no selection, so the caret has to be
  // read from the control inside their shadow root. Reading it is safe: a shadow
  // root the component did not attach, or a browser that reports no selection,
  // both come back null and the token is appended instead.
  function caretOf(host: CaretHost | null): number | null {
    const inner = host?.shadowRoot?.querySelector('input, textarea');
    if (!(inner instanceof HTMLInputElement) && !(inner instanceof HTMLTextAreaElement)) {
      return null;
    }
    return inner.selectionStart;
  }

  function insert(path: string): void {
    const token = `{{${path}}}`;
    const host =
      activeTarget === 'titleTpl' ? titleRef : activeTarget === 'bodyTpl' ? bodyRef : metadataRef;
    const caret = target === null ? null : caretOf(host.current);
    edit({ [activeTarget]: insertAtCaret(form[activeTarget], caret, token) });
  }

  return (
    <FormPage
      breadcrumb={[
        { label: t('alerts.section.templates'), href: '/system-admin/alert-templates' },
        { label: isNew ? t('alerts.templates.new_crumb') : t('alerts.templates.edit_crumb') },
      ]}
      title={isNew ? t('alerts.templates.new') : t('alerts.templates.edit')}
      lede={t('alerts.templates.form.lede')}
      saveLabel={isNew ? t('alerts.templates.action.create') : t('alerts.templates.action.save')}
      isSaving={save.isPending}
      isDirty={dirty}
      isLoading={templates.isLoading}
      loadError={
        templates.isError
          ? loadFailureKind(templates.error)
          : !isNew && templates.data && !existing
            ? 'not_found'
            : null
      }
      refusal={refusal}
      onSave={handleSave}
      onCancel={() => navigate('/system-admin/alert-templates')}
    >
      <FormSection title={t('alerts.templates.form.content')}>
        <FormField
          label={t('alerts.templates.field.display_name')}
          required
          error={submitted ? displayNameError : undefined}
        >
          <MudTextInput
            size="md"
            value={form.displayName}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              edit({ displayName: e.detail.value })
            }
          />
        </FormField>

        <FormField label={t('alerts.templates.field.locale')} required>
          <MudSelect
            size="medium"
            value={form.locale}
            onMudChange={(e: CustomEvent<{ value: string }>) => edit({ locale: e.detail.value })}
          >
            {localeOptions(form.locale).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </MudSelect>
        </FormField>

        <FormField
          label={t('alerts.templates.field.title_tpl')}
          required
          fullWidth
          error={submitted ? titleError : undefined}
        >
          <MudTextInput
            ref={(el: CaretHost | null) => {
              titleRef.current = el;
            }}
            size="md"
            value={form.titleTpl}
            onMudFocus={() => setTarget('titleTpl')}
            onMudInput={(e: CustomEvent<{ value: string }>) => edit({ titleTpl: e.detail.value })}
          />
        </FormField>

        <FormField
          label={t('alerts.templates.field.body_tpl')}
          required
          fullWidth
          error={submitted ? bodyError : undefined}
        >
          <MudTextarea
            ref={(el: CaretHost | null) => {
              bodyRef.current = el;
            }}
            rows={7}
            resize="vertical"
            value={form.bodyTpl}
            onMudFocus={() => setTarget('bodyTpl')}
            onMudInput={(e: CustomEvent<{ value: string }>) => edit({ bodyTpl: e.detail.value })}
          />
        </FormField>

        <FormField
          label={t('alerts.templates.field.metadata')}
          info={t('alerts.templates.field.metadata_info')}
          hint={t('alerts.templates.field.metadata_hint')}
          fullWidth
        >
          <MudTextarea
            ref={(el: CaretHost | null) => {
              metadataRef.current = el;
            }}
            rows={3}
            resize="vertical"
            value={form.metadataText}
            onMudFocus={() => setTarget('metadataText')}
            onMudInput={(e: CustomEvent<{ value: string }>) =>
              edit({ metadataText: e.detail.value })
            }
          />
        </FormField>

        {/* The wireframe's "Valori din evaluare", titled «Placeholdere disponibile»
            (TC-UC27-02-01): the substituents are buttons
            that write into the field the administrator was last in, and the
            hint says which one that is. The register's dialog listed the same
            paths as text to be copied by hand. */}
        <FormField
          label={t('alerts.templates.form.substituents')}
          hint={t('alerts.templates.form.substituents_target', {
            field: t(INSERT_TARGET_KEYS[activeTarget]),
          })}
          fullWidth
        >
          {/* The wireframe's `.subst` (mud.css:5945): small outlined chips that
              write into the field the administrator was last in. */}
          <div className="simsm-alerts__substituents">
            {TEMPLATE_PLACEHOLDERS.map((group) => (
              <div key={group.labelKey} className="simsm-alerts__substituent-group">
                <span className="simsm-alerts__meta-key">{t(group.labelKey)}</span>
                <div className="simsm-alerts__substituent-row">
                  {group.paths.map((path) => (
                    <MudButton
                      shape="circular"
                      key={path}
                      variant="strict"
                      appearance="outlined"
                      size="sm"
                      label={t('alerts.templates.form.insert_substituent', {
                        path,
                        field: t(INSERT_TARGET_KEYS[activeTarget]),
                      })}
                      onClick={() => insert(path)}
                    >
                      {`{{${path}}}`}
                    </MudButton>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </FormField>
      </FormSection>
    </FormPage>
  );
}
